/* eslint-disable @typescript-eslint/no-explicit-any */
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import * as React from 'react';
import Select, {
  ClearIndicatorProps,
  GroupBase,
  InputProps,
  MenuListProps,
  MultiValueProps,
  OnChangeValue,
  OptionProps,
  PlaceholderProps,
  Props,
  SingleValueProps,
  components,
} from 'react-select';
import { Badge, Button, Icon, Tooltip, isValidIcon } from '../index';
import './style.scss';
import { ISelectOption } from './types';
import CreatableSelect, { CreatableProps } from 'react-select/creatable';
// import { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager';

const PORTAL_ELEMENT = document.querySelector('#select-portal') as HTMLElement;

export type SelectProps<
  IsMulti extends boolean,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Pick<
  Props<Option, IsMulti, Group>,
  | 'isSearchable'
  | 'isClearable'
  | 'isDisabled'
  | 'isOptionDisabled'
  | 'isOptionSelected'
  | 'isLoading'
  | 'isMulti'
  | 'id'
  | 'onChange'
  | 'onInputChange'
  | 'defaultValue'
  | 'value'
  | 'placeholder'
  | 'classNamePrefix'
  | 'filterOption'
  | 'onFocus'
  | 'onMenuClose'
  | 'inputValue'
  | 'components'
  | 'className'
  | 'noOptionsMessage'
> & {
  additionalQueryKeys?: Array<string>;
  name: string;
  options: Array<Option | Group>;
  required?: boolean;
  isError?: boolean;
  size?: 'sm' | 'md';
  wrapItems?: boolean;
  withCheckboxes?: boolean;
  limit?: number;
  showCount?: boolean | ((total: number) => string);
  maxShown?: number;
  countMessage?: (count: number) => string | React.ReactNode;
  showSelectAllButtons?: {
    enabled?: boolean;
    selectAll?: string;
    unselectAll?: string;
  };
  ref?: React.RefObject<any> | ((instance: any) => void);
  creatable?: Pick<
    CreatableProps<Option, IsMulti, Group>,
    'createOptionPosition' | 'onCreateOption' | 'formatCreateLabel'
  >;
} & React.RefAttributes<HTMLSelectElement>;

function CustomOption<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: OptionProps<Option, IsMulti, Group> & {
    size: 'sm' | 'md';
  },
) {
  /* @ts-expect-error react-select doesn't provide a way extend selectProps in OptionProps  */
  const withCheckboxes = props.selectProps.withCheckboxes;
  return (
    <span
      // @ts-expect-error data-cy is not a valid attr
      data-cy={`${String(props.selectProps['data-cy'] ?? props.selectProps.id)}.${
        props.data.label || props.data.value
      }`}
      key={props.innerProps.key}
    >
      <components.Option
        {...props}
        className={'d-flex align-items-center space-x-2 position-relative'}
      >
        {props.isMulti && withCheckboxes && (
          <input
            type='checkbox'
            checked={props.isSelected}
            onChange={() => {}}
          />
        )}
        {isValidIcon(props.data.icon) ? (
          <Icon icon={props.data.icon} size={props.size} />
        ) : (
          props.data.icon
        )}
        <div
          style={{ width: '100%' }}
          className={clsx('react-select__option-text', props.size)}
        >
          {props.data.label}
        </div>
        {props.isSelected && !withCheckboxes && (
          <Icon
            icon='check-mark-line'
            size={'sm'}
            className='text-primary position-absolute'
            style={{ right: 5 }}
          />
        )}
      </components.Option>
    </span>
  );
}

function CustomSingleValueProps<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: SingleValueProps<Option, IsMulti, Group>) {
  return (
    <components.SingleValue
      {...props}
      innerProps={{
        ...props.innerProps,
        // @ts-expect-error data-cy is not a valid attr
        'data-cy': `${String(props.selectProps['data-cy'] ?? props.selectProps.id)}.value`,
      }}
    />
  );
}
function CustomMultiValueProps<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: MultiValueProps<Option, IsMulti, Group>) {
  const value = props.getValue();
  // @ts-expect-error data-cy is not a valid attr
  const maxShown = (props.selectProps['maxShown'] as number | undefined) ?? 1;
  const getCountMessage =
    // @ts-expect-error data-cy is not a valid attr
    props.selectProps['countMessage'] as
      | ((count: number) => string | React.ReactNode)
      | undefined;
  // @ts-expect-error data-cy is not a valid attr
  const dataCy = String(props.selectProps['data-cy'] ?? props.selectProps.id);
  const shouldShowCount = value.length > maxShown;
  if (!shouldShowCount) {
    return (
      <components.MultiValue
        {...props}
        innerProps={{
          ...props.innerProps,
          // @ts-expect-error data-cy is not a valid attr
          'data-cy': `${dataCy}.${props.data.label}.value`,
        }}
      >
        {props.data.groupLabel && (
          <Badge pill bg='light-gray'>
            {props.data.groupLabel}
          </Badge>
        )}
        {props.children}
      </components.MultiValue>
    );
  }
  // NEW BEHAVIOR: show count badge only with some message
  if (shouldShowCount && getCountMessage) {
    const selectedValues = props.getValue();
    if (props.index === 0) {
      return (
        <Tooltip
          label={selectedValues.map((option) => option.label).join(', ')}
        >
          <Badge pill bg='light-gray' id={`${dataCy}.multi-value-count`}>
            {getCountMessage(selectedValues.length)}
          </Badge>
        </Tooltip>
      );
    }
    return <></>;
  }
  // OLD BEHAVIOR: show +N badge
  // for now, Design teams requested to keep handling new AND old behavior for a while
  // that's why the old code is still there :(
  if (props.index < maxShown)
    return (
      <components.MultiValue
        {...props}
        innerProps={{
          ...props.innerProps,
          // @ts-expect-error data-cy is not a valid attr
          'data-cy': `${dataCy}.${props.data.label}.value`,
        }}
      >
        {props.data.groupLabel && (
          <Badge pill bg='light-gray' id={`${dataCy}.multi-value-count`}>
            {props.data.groupLabel}
          </Badge>
        )}
        {props.children}
      </components.MultiValue>
    );
  if (props.index === maxShown) {
    const selectedValues = props.getValue();
    return (
      <Tooltip label={selectedValues.map((option) => option.label).join(',')}>
        <Badge pill bg='light-gray' id={`${dataCy}.multi-value-count`}>
          +{selectedValues.length - maxShown}
        </Badge>
      </Tooltip>
    );
  }
  return <></>;
}
function InnerCustomInputProps<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: InputProps<Option, IsMulti, Group>) {
  return (
    <components.Input
      {...props}
      data-cy={`${props.selectProps.name as string}.input`}
    />
  );
}

function MenuList<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ children, options, ...props }: MenuListProps<Option, IsMulti, Group>) {
  /* @ts-expect-error react-select doesn't provide a way extend selectProps in OptionProps  */
  const limit = props.selectProps?.limit as SelectProps<
    IsMulti,
    Option,
    Group
  >['limit'];
  /* @ts-expect-error react-select doesn't provide a way extend selectProps in OptionProps  */
  const shouldShowCount = props.selectProps?.showCount as SelectProps<
    IsMulti,
    Option,
    Group
  >['showCount'];
  const getCountMessage =
    // @ts-expect-error data-cy is not a valid attr
    props.selectProps['countMessage'] as
      | ((count: number) => string | React.ReactNode)
      | undefined;

  const showSelectAllButtons =
    /* @ts-expect-error react-select doesn't provide a way extend selectProps in MenuListProps  */
    props.selectProps?.showSelectAllButtons as
      | {
          enabled?: boolean;
          selectAll?: string;
          unselectAll?: string;
        }
      | undefined;

  const selectedValues = props.getValue();

  const flattenOptions = (opts: typeof options) => {
    const flattened: Array<Option> = [];
    opts.forEach((opt) => {
      if ('options' in opt) {
        flattened.push(...(opt.options as Array<Option>));
      } else {
        flattened.push(opt);
      }
    });
    return flattened;
  };

  const handleSelectAll = () => {
    const allOptions = flattenOptions(options);
    props.selectProps.onChange?.(
      allOptions as unknown as OnChangeValue<Option, IsMulti>,
      {
        action: 'select-option',
        option: undefined,
      },
    );
  };

  const handleClearAll = () => {
    props.selectProps.onChange?.(
      [] as unknown as OnChangeValue<Option, IsMulti>,
      {
        action: 'clear',
        removedValues: selectedValues,
      },
    );
  };

  return (
    <components.MenuList {...props} options={options}>
      <>
        {showSelectAllButtons?.enabled && (
          <div className='react-select__menu-actions d-flex justify-content-end align-items-center space-x-2 px-2 py-1 border-bottom'>
            <Button
              id={`select-all-button-${props.selectProps.name as string}`}
              type='button'
              variant='neutral'
              size='xs'
              onClick={handleSelectAll}
              onMouseDown={(e) => e.preventDefault()}
            >
              {showSelectAllButtons?.selectAll || 'Select all'}
            </Button>
            <Button
              id={`clear-all-button-${props.selectProps.name as string}`}
              type='button'
              variant='neutral'
              size='xs'
              onClick={handleClearAll}
              onMouseDown={(e) => e.preventDefault()}
            >
              {showSelectAllButtons?.unselectAll || 'Clear all'}
            </Button>
          </div>
        )}
        {!isNil(limit) && Array.isArray(children)
          ? children.slice(0, limit)
          : children}
        {shouldShowCount && getCountMessage && (
          <div className='px-2 bg-neutral-grey-1 text-neutral-grey-5'>
            {getCountMessage(props.getValue().length)}
          </div>
        )}
      </>
    </components.MenuList>
  );
}

const CustomInputProps = React.memo(
  InnerCustomInputProps,
) as typeof InnerCustomInputProps;

function CustomClearIndicator<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: ClearIndicatorProps<Option, IsMulti, Group>) {
  const clearValue = () => {
    props.clearValue();
  };

  return (
    <components.ClearIndicator
      {...props}
      innerProps={{
        ...props.innerProps,
        // @ts-expect-error data-cy is not a valid attr
        'data-cy': `${String(props.selectProps['data-cy'] ?? props.selectProps.id)}.clear`,
        'onClick': clearValue,
      }}
    />
  );
}

function CustomPlaceholder<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: PlaceholderProps<Option, IsMulti, Group>) {
  return (
    <components.Placeholder
      {...props}
      innerProps={{
        ...props.innerProps,
        // @ts-expect-error data-cy is not a valid attr
        'data-cy': `${String(props.selectProps['data-cy'] ?? props.selectProps.id)}.placeholder`,
      }}
    />
  );
}

// https://github.com/JedWatson/react-select/issues/977
const CustomMenuList = <
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>,
) => <MenuList {...props} />;

function ReactSelectWrapper<
  IsMulti extends boolean,
  Option extends ISelectOption<string | number> = ISelectOption<
    string | number
  >,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ size = 'sm', creatable, ...props }: SelectProps<IsMulti, Option, Group>) {
  const isCreatable = !isNil(creatable);

  const selectProps: Props<Option, IsMulti, Group> = {
    ...props,
    onChange: (...args) => {
      props.onChange?.(...args);
    },
    closeMenuOnSelect: !props.isMulti,
    classNamePrefix: 'react-select',
    hideSelectedOptions: !props.withCheckboxes && props.isMulti,
    menuPortalTarget: PORTAL_ELEMENT,
    classNames: {
      valueContainer: (controlProps) => clsx(controlProps.className),
      dropdownIndicator: (controlProps) => clsx(controlProps.className, size),
      control: (controlProps) =>
        clsx(controlProps.className, size, {
          'is-invalid': props.isError,
          'wrap-items': props.wrapItems,
        }),
      menuList: (controlProps) => clsx(controlProps.className, size),
    },
    components: {
      Option:
        props.components?.Option ||
        ((props) => <CustomOption {...props} size={size} />),
      SingleValue:
        props.components?.SingleValue ||
        ((props) => <CustomSingleValueProps {...props} />),
      MultiValue: (props) => <CustomMultiValueProps {...props} />,
      Input: CustomInputProps,
      MenuList: CustomMenuList,
      ClearIndicator: CustomClearIndicator,
      Placeholder: CustomPlaceholder,
    },
    styles: {
      // @ts-expect-error react-select has a type issue with the styles prop after latest React types update
      menuPortal: (props) => ({
        ...props,
        zIndex: 1056, // should be greater than --falcon-modal-zindex (1055)
      }),
      // stretch the (otherwise min-content) input across the value container so a
      // right-click always lands on the editable input, exposing the native
      // cut/copy/paste context menu instead of the container/placeholder
      // @ts-expect-error react-select has a type issue with the styles prop after latest React types update
      input: (base) => ({
        ...base,
        gridTemplateColumns: '0 1fr',
      }),
    },
  };

  return (
    <div className={'react-select-wrapper'}>
      {isCreatable ? (
        <CreatableSelect<Option, IsMulti, Group>
          {...selectProps}
          {...creatable}
        />
      ) : (
        <Select<Option, IsMulti, Group> {...selectProps} />
      )}
    </div>
  );
}

export default ReactSelectWrapper;
